import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Search,
  RefreshCw,
  UserPlus,
  Download,
  X,
  Trash2,
  MoreHorizontal,
  Mail,
  CreditCard,
  Settings,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { useAdminClients } from "../hooks/useAdminClients";
import { exportToCSV } from "../utils/export";
import { supabase } from "../integrations/supabase/client";

interface Client {
  id: string;
  email: string;
  name: string;
  company?: string;
  plan: string;
  status: string;
  messages_sent: number;
  created_at: string;
  last_active?: string;
}

const planColors: Record<string, string> = {
  starter: "bg-slate-500/20 text-slate-400",
  pro: "bg-blue-500/20 text-blue-400",
  business: "bg-purple-500/20 text-purple-400",
  enterprise: "bg-amber-500/20 text-amber-400",
};

const statusColors: Record<string, string> = {
  active: "bg-emerald-500/20 text-emerald-400",
  trialing: "bg-blue-500/20 text-blue-400",
  past_due: "bg-amber-500/20 text-amber-400",
  canceled: "bg-red-500/20 text-red-400",
  unpaid: "bg-red-500/20 text-red-400",
};

export function Clients() {
  const { clients: adminUsers, loading, fetchClients } = useAdminClients();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [newClientForm, setNewClientForm] = useState({
    email: "",
    company: "",
    plan: "starter",
    isTrial: false,
    trialDays: 14,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };
    if (openMenuId) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openMenuId]);

  const clients: Client[] = adminUsers.map((u) => ({
    id: u.id,
    email: u.email ?? "",
    name: u.company_name ?? u.email ?? "Cliente",
    company: u.company_name ?? undefined,
    plan: (u.plan || "starter").toLowerCase(),
    status: u.status || "active",
    messages_sent: 0,
    created_at: u.created_at,
    last_active: u.last_sign_in_at,
  }));

  const filteredClients = clients.filter((client) => {
    const matchesSearch =
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.company?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesPlan = filterPlan === "all" || client.plan === filterPlan;
    const matchesStatus =
      filterStatus === "all" || client.status === filterStatus;

    return matchesSearch && matchesPlan && matchesStatus;
  });

  const handleExportClients = () => {
    exportToCSV(
      filteredClients as unknown as Record<string, unknown>[],
      "clientes",
      [
        { key: "id", label: "ID" },
        { key: "email", label: "Email" },
        { key: "name", label: "Nome" },
        { key: "company", label: "Empresa" },
        { key: "plan", label: "Plano" },
        { key: "status", label: "Status" },
        { key: "created_at", label: "Criado em" },
      ],
    );
  };

  const handleSendEmail = (_userId: string) => {
    // TODO: Criar modal SendEmailDialog
    toast.info("Funcionalidade de envio de email em desenvolvimento");
    setOpenMenuId(null);
  };

  const handleCreateInvoice = (_userId: string) => {
    // TODO: Criar modal CreateInvoiceDialog
    toast.info("Funcionalidade de criação de fatura em desenvolvimento");
    setOpenMenuId(null);
  };

  const handleDeleteClient = async (userId: string) => {
    if (
      !confirm(
        "Tem certeza que deseja deletar este cliente? Esta ação não pode ser desfeita.",
      )
    ) {
      return;
    }

    const reason = prompt("Motivo da exclusão (opcional):") || undefined;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sessão expirada. Faça login novamente.");
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/admin-delete-user`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: userId,
            reason,
            cancel_asaas_subscription: true,
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        toast.success("Cliente deletado com sucesso");
        fetchClients(); // Atualizar lista
      } else {
        toast.error(data.error || "Erro ao deletar cliente");
      }
    } catch (error) {
      console.error("Erro ao deletar cliente:", error);
      toast.error("Falha ao deletar cliente");
    }
  };

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sessão expirada. Faça login novamente.");
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // Chamar Edge Function para enviar convite com trial se necessário
      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-team-invite`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY || "",
          },
          body: JSON.stringify({
            email: newClientForm.email,
            role: "admin", // Cliente recebe role admin por padrão
            name: newClientForm.company || newClientForm.email.split("@")[0],
            invitedBy: session.user.id,
            inviteUrl: `${window.location.origin.replace("admin.", "")}/dashboard`,
            isTrial: newClientForm.isTrial,
            trialDays: newClientForm.isTrial
              ? newClientForm.trialDays
              : undefined,
            plan: newClientForm.plan,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erro ao enviar convite");
      }

      await response.json();

      toast.success(
        newClientForm.isTrial
          ? `Convite com trial de ${newClientForm.trialDays} dias enviado para ${newClientForm.email}!`
          : `Convite enviado para ${newClientForm.email}!`,
      );

      setShowNewClientModal(false);
      setNewClientForm({
        email: "",
        company: "",
        plan: "starter",
        isTrial: false,
        trialDays: 14,
      });
      fetchClients();
 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      toast.error(err.message || "Erro ao criar cliente");
      console.error(err);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Gestão de Clientes
          </h1>
          <p className="text-slate-400">
            {clients.length} clientes cadastrados •{" "}
            {clients.filter((c) => c.status === "active").length} ativos
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleExportClients}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-300 hover:bg-white/10 transition-all"
          >
            <Download className="w-4 h-4" />
            Exportar
          </button>
          <button
            type="button"
            onClick={() => setShowNewClientModal(true)}
            className="flex items-center gap-2 px-4 py-2 admin-gradient rounded-lg text-white font-medium hover:opacity-90 transition-all"
          >
            <UserPlus className="w-4 h-4" />
            Novo Cliente
          </button>
        </div>
      </div>

      {showNewClientModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowNewClientModal(false)}
          />
          <div className="relative bg-[#0a0a0a] border border-white/10 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Novo Cliente</h2>
              <button
                type="button"
                onClick={() => setShowNewClientModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateClient} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={newClientForm.email}
                  onChange={(e) =>
                    setNewClientForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                  placeholder="cliente@empresa.com"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Empresa
                </label>
                <input
                  type="text"
                  value={newClientForm.company}
                  onChange={(e) =>
                    setNewClientForm((prev) => ({
                      ...prev,
                      company: e.target.value,
                    }))
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                  placeholder="Nome da empresa"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Plano
                </label>
                <select
                  value={newClientForm.plan}
                  onChange={(e) =>
                    setNewClientForm((prev) => ({
                      ...prev,
                      plan: e.target.value,
                    }))
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                >
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="business">Business</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              {/* Trial Option */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newClientForm.isTrial}
                    onChange={(e) =>
                      setNewClientForm((prev) => ({
                        ...prev,
                        isTrial: e.target.checked,
                      }))
                    }
                    className="w-4 h-4 rounded border-white/10 bg-white/5 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-300">
                    Enviar convite com período de trial
                  </span>
                </label>

                {newClientForm.isTrial && (
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      Dias de Trial
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="90"
                      value={newClientForm.trialDays}
                      onChange={(e) =>
                        setNewClientForm((prev) => ({
                          ...prev,
                          trialDays: parseInt(e.target.value) || 14,
                        }))
                      }
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                      placeholder="14"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewClientModal(false)}
                  className="flex-1 px-4 py-2 border border-white/10 rounded-lg text-white hover:bg-white/5"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white"
                >
                  Criar Cliente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nome, email ou empresa..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-red-500 transition-all"
          />
        </div>

        <select
          value={filterPlan}
          onChange={(e) => setFilterPlan(e.target.value)}
          className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500 transition-all"
        >
          <option value="all">Todos os Planos</option>
          <option value="starter">Starter</option>
          <option value="pro">Pro</option>
          <option value="business">Business</option>
          <option value="enterprise">Enterprise</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500 transition-all"
        >
          <option value="all">Todos os Status</option>
          <option value="active">Ativo</option>
          <option value="trialing">Trial</option>
          <option value="past_due">Pendente</option>
          <option value="canceled">Cancelado</option>
        </select>

        <button
          onClick={fetchClients}
          disabled={loading}
          className="p-2.5 bg-white/5 border border-white/10 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Table */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <Users className="w-12 h-12 mb-4 opacity-50" />
            <p>Nenhum cliente encontrado</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Plano
                </th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Mensagens
                </th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Cadastro
                </th>
                <th className="text-right py-4 px-6 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredClients.map((client) => (
                <tr
                  key={client.id}
                  className="hover:bg-white/5 transition-colors cursor-pointer"
                  onClick={() => navigate(`/clients/${client.id}`)}
                >
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-bold">
                        {client.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-white">{client.name}</p>
                        <p className="text-sm text-slate-400">{client.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${planColors[client.plan]}`}
                    >
                      {client.plan}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColors[client.status]}`}
                    >
                      {client.status === "active"
                        ? "Ativo"
                        : client.status === "trialing"
                          ? "Trial"
                          : client.status === "past_due"
                            ? "Pendente"
                            : client.status === "canceled"
                              ? "Cancelado"
                              : client.status}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <p className="text-white">
                      {client.messages_sent.toLocaleString()}
                    </p>
                  </td>
                  <td className="py-4 px-6">
                    <p className="text-slate-400 text-sm">
                      {format(new Date(client.created_at), "dd MMM yyyy", {
                        locale: ptBR,
                      })}
                    </p>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="relative flex items-center justify-end">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(
                            openMenuId === client.id ? null : client.id,
                          );
                        }}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <MoreHorizontal className="w-4 h-4 text-slate-400" />
                      </button>
                      {openMenuId === client.id && (
                        <div className="absolute right-0 top-10 z-50 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-lg min-w-[200px]">
                          <div className="py-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/clients/${client.id}`);
                                setOpenMenuId(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/5 flex items-center gap-2"
                            >
                              <Settings className="w-4 h-4" />
                              Ver Detalhes
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendEmail(client.id);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/5 flex items-center gap-2"
                            >
                              <Mail className="w-4 h-4" />
                              Enviar Email
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCreateInvoice(client.id);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/5 flex items-center gap-2"
                            >
                              <CreditCard className="w-4 h-4" />
                              Criar Fatura
                            </button>
                            <div className="border-t border-white/10 my-1" />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClient(client.id);
                                setOpenMenuId(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Deletar Cliente
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
